import { useState } from 'react'
import { match } from 'ts-pattern'
import { type NewerScanBannerState } from '../components/newer-scan-banner/newer-scan-banner.tsx'
import { useGetScanFindings, useGetScans } from '../utils/api-client/api-hooks.ts'
import { type Tool } from '../utils/api-client/api-schemas.ts'

export type UseNewerScanBannerResult = {
  newerScanId: string
  state: NewerScanBannerState
  completedAnalysis: number
  total: number
  isDismissed: boolean
  dismiss: () => void
  newerScanImportedAt?: string
}

export function useNewerScanBanner({
  currentScanId,
  repositoryId,
  tool,
  branch,
}: {
  currentScanId: string
  repositoryId?: string
  tool?: Tool
  /**
   * Only scans on this branch count as newer; `undefined` for every branch. Required, so a caller
   * opts out of the branch scope explicitly rather than by leaving it off.
   */
  branch: string | undefined
}): UseNewerScanBannerResult | null {
  const [isDismissed, setIsDismissed] = useState(false)

  const { data: latestScans } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: repositoryId ? [repositoryId] : undefined,
    tools: tool ? [tool] : undefined,
    branch,
    enabled: !!repositoryId && !!tool,
    refetchInterval: 5_000,
  })

  const latestScan = extractLatestScan(latestScans)
  const isNewer = isNewerScanId(latestScan?.id, currentScanId)

  const { data: newerScanFindings } = useGetScanFindings({
    scanId: isNewer ? latestScan?.id : undefined,
    pagination: { pageIndex: 0, pageSize: 1 },
    enabled: isNewer,
    refetchInterval: isNewer ? 5_000 : false,
  })

  if (!isNewer || !latestScan || !newerScanFindings) return null

  return buildResult(latestScan, newerScanFindings, isDismissed, () => setIsDismissed(true))
}

const extractLatestScan = (
  latestScans: { _embedded?: { items?: { id: string }[] } } | undefined
): { id: string } | null => {
  const latestScan = latestScans?._embedded?.items?.[0]
  return latestScan ? { id: latestScan.id } : null
}

const isNewerScanId = (latestScanId: string | undefined, currentScanId: string): boolean =>
  !!latestScanId && latestScanId !== currentScanId

const buildResult = (
  latestScan: { id: string; imported_at?: string | null },
  findings: { completed_analysis: number; in_progress_analysis: number; total: number },
  isDismissed: boolean,
  dismiss: () => void
): UseNewerScanBannerResult => ({
  newerScanId: latestScan.id,
  state: deriveBannerState({
    completed_analysis: findings.completed_analysis,
    in_progress_analysis: findings.in_progress_analysis,
  }),
  completedAnalysis: findings.completed_analysis,
  total: findings.total,
  isDismissed,
  dismiss,
  newerScanImportedAt: latestScan.imported_at ?? undefined,
})

const deriveBannerState = ({
  completed_analysis,
  in_progress_analysis,
}: {
  completed_analysis: number
  in_progress_analysis: number
}): NewerScanBannerState =>
  match({ completed_analysis, in_progress_analysis })
    .with({ completed_analysis: 0, in_progress_analysis: 0 }, () => 'imported' as const)
    .when(
      ({ in_progress_analysis: inProgress, completed_analysis: completed }) => inProgress > 0 && completed === 0,
      () => 'analyzing' as const
    )
    .otherwise(() => 'has-analysis' as const)
